"use client";

import type { LucideIcon } from "lucide-react";
import { Info } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

type Accent = "emerald" | "blue" | "amber" | "violet" | "red" | "slate";

interface MetricCardProps {
  title: string;
  value: string;
  subtitle: string;
  icon: LucideIcon;
  trend?: "up" | "down" | "neutral";
  /** Colored icon-badge + value accent. Defaults from `trend` when omitted. */
  accent?: Accent;
  // When present, shows an info icon + a "Sold X · UW Y" tooltip split.
  breakdown?: { sold: string; uw: string };
  /** A second figure shown inside the same card, under the headline. Used for
   *  the sold/production pair so both read without a second tile. */
  subMetric?: { label: string; value: string };
  // Plain-text info tooltip explaining what the metric counts/filters.
  tooltip?: string;
}

const ACCENT: Record<Accent, { bg: string; text: string }> = {
  emerald: { bg: "bg-emerald-500/10", text: "text-emerald-500" },
  blue: { bg: "bg-blue-500/10", text: "text-blue-500" },
  amber: { bg: "bg-amber-500/10", text: "text-amber-500" },
  violet: { bg: "bg-violet-500/10", text: "text-violet-500" },
  red: { bg: "bg-red-500/10", text: "text-red-500" },
  slate: { bg: "bg-slate-500/10", text: "text-slate-500" },
};

export const MetricCard = function ({
  title,
  value,
  subtitle,
  icon: Icon,
  trend = "neutral",
  accent,
  breakdown,
  subMetric,
  tooltip,
}: MetricCardProps) {
  // Card look mirrors the Agency Dashboard summary tiles: soft card, colored
  // icon badge, big tabular value. Accent defaults from trend so existing
  // call sites get sensible colors without changes.
  // Icon badge uses the PRIMARY token by default so white-label agencies get
  // their own brand color. Only an explicit `accent` overrides it.
  const a = accent
    ? ACCENT[accent]
    : { bg: "bg-primary/10", text: "text-primary" };
  // Subtitle carries the delta (e.g. "+2 vs prior", "-$2,774 vs prior"), so keep
  // it trend-colored — green up, red down, muted neutral.
  const subtitleColor =
    trend === "up" ? "text-green-600" : trend === "down" ? "text-red-600" : "text-muted-foreground";

  return (
    <div className="group relative flex h-full w-full flex-col rounded-xl border bg-card/70 p-3.5 transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          {title}
          {tooltip && !breakdown && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label="What this counts" className="inline-flex items-center cursor-help">
                    <Info className="h-3.5 w-3.5 text-gray-400 transition-colors hover:text-primary" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[260px] text-xs">
                  {tooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {breakdown && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label="Breakdown" className="inline-flex items-center cursor-help">
                    <Info className="h-3.5 w-3.5 text-primary/80 transition-colors hover:text-primary" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  <div className="flex flex-col gap-1">
                    <span className="mb-0.5 font-semibold">Breakdown</span>
                    <span className="flex items-center justify-between gap-3">
                      <span className="text-emerald-400">Sold &amp; Approved</span>
                      <span className="font-semibold tabular-nums">{breakdown.sold}</span>
                    </span>
                    <span className="flex items-center justify-between gap-3">
                      <span className="text-amber-400">Underwriting</span>
                      <span className="font-semibold tabular-nums">{breakdown.uw}</span>
                    </span>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </span>
        <Icon className={`h-5 w-5 shrink-0 ${a.text}`} />
      </div>
      <div className="mt-2 truncate text-2xl font-bold tabular-nums tracking-tight">
        {value}
      </div>
      {/* The second figure rides the delta line rather than sitting in a block
          of its own — a card that grew taller than its neighbours left them
          looking half-empty, and every card in the row shares one height. */}
      <div className="mt-1 flex items-baseline justify-between gap-2">
        <p className={`truncate text-[11px] leading-tight ${subtitleColor}`}>{subtitle}</p>
        {!!subMetric && (
          <span className="shrink-0 whitespace-nowrap text-[11px] leading-tight text-muted-foreground">
            {subMetric.label}{" "}
            <span className="font-semibold tabular-nums text-foreground">{subMetric.value}</span>
          </span>
        )}
      </div>
    </div>
  );
};
